import { DetectedEvent } from '@ais-ui/websockets';
import React, { useEffect, useState } from 'react';
import { cn, timeSince } from '@ais-ui/utils';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { useEventSeverity } from './useEventSeverity';
import {
  AccordionItem,
  AccordionTrigger,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiCheckCircleThin, PiWarningCircleThin } from 'react-icons/pi';
import { EventDropDownMenu } from './EventDropdownMenu';
import { EventItemContent } from './EventItemContent';

interface EventItemProps {
  event: DetectedEvent;
}

export const EventItem = ({ event }: EventItemProps) => {
  const [timeAgo, setTimeAgo] = useState<string>(timeSince(event.act_ts));

  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTimeAgo(timeSince(event.act_ts));
    }, 5000);

    return () => clearInterval(intervalId); // Cleanup interval on component unmount
  }, [event.act_ts]);

  const severity = useEventSeverity(event);

  return (
    <AccordionItem key={event.id} value={event.id} className={cn('px-4')}>
      <div className="flex justify-between items-center">
        <div className="flex-grow">
          <div className="flex items-center w-full">
            {event.ack_state === 1 && (
              <Tooltip>
                <TooltipTrigger>
                  <PiWarningCircleThin className="w-8 h-8 text-yellow-600"></PiWarningCircleThin>
                </TooltipTrigger>
                <TooltipContent>Not acknowledged</TooltipContent>
              </Tooltip>
            )}
            {event.ack_state === 2 && (
              <Tooltip>
                <TooltipTrigger>
                  <PiCheckCircleThin className="w-8 h-8 text-green-600"></PiCheckCircleThin>
                </TooltipTrigger>
                <TooltipContent>
                  <div>
                    <h3 className="font-medium text-base text-left">
                      Acknowledged
                    </h3>
                    <Separator className="my-2"></Separator>
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <p className="text-sm font-medium flex-none">By</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-ellipsis overflow-hidden">
                          {event.ack_state_modified_by}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <p className="text-sm font-medium flex-none">At</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-ellipsis overflow-hidden">
                          {formatDateTimeWithTimeZone(
                            event.ack_state_modified_ts
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            )}
            <AccordionTrigger className="w-full" hideChevron={true}>
              <div className="ml-3 space-y-1">
                <p className="text-xs text-left font-medium leading-none group-hover:underline">
                  {event.name} - {event.shipName}
                </p>
                <div className="flex items-center space-x-1.5">
                  <div
                    className={cn(
                      'w-2 h-2 rounded-full',
                      severity === 'note' && 'bg-blue-500',
                      severity === 'warning' && 'bg-yellow-500',
                      severity === 'alert' && 'bg-red-500'
                    )}
                  ></div>
                  <p className="text-sm text-left text-muted-foreground">
                    {timeAgo}
                  </p>
                </div>
              </div>
            </AccordionTrigger>
          </div>
        </div>
        <EventDropDownMenu event={event}></EventDropDownMenu>
      </div>

      <EventItemContent event={event}></EventItemContent>
    </AccordionItem>
  );
};
